import { Common } from './common.model';
import {
    Embedded,
    EmbeddedUser,
    toUserEmbedded,
    EmbeddedProject,
    toProjectsEmbedded,
    EmbeddedCell,
    toCellEmbedded,
    EmbeddedTribe,
    toTribeEmbedded,
    toCompanyEmbedded,
    EmbeddedCompany,
} from './embed.model';
import { Link } from './link.model';
import { Server } from './server.model';

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $projects: EmbeddedProject[];
    $cell: EmbeddedCell;
    $tribe: EmbeddedTribe;
    $company: EmbeddedCompany;
}

export type EmbedInventory<T extends embed> = Inventory & Partial<Pick<EmbedProperties, T>>;

export interface EmbedInventories<T extends embed> {
    embedInventories: EmbedInventory<T>[];
    totalSize?: number;
}

export interface LinkedInventories {
    inventory: Inventory[];
    totalSize: number;
}

export const configuration: {[K in embed]: {embed: string; transform: (inventory: Inventory, embedded: Embedded) => EmbedProperties[K] }} = {
    $createdBy: {
        embed: 'users',
        transform: (data: Inventory, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Inventory, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: Inventory, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $projects: {
        embed: 'projects',
        transform: (data: Inventory, embedded: Embedded): EmbeddedProject[] => toProjectsEmbedded(data, 'projects', embedded),
    },
    $cell: {
        embed: 'cells',
        transform: (data: Inventory, embedded: Embedded): EmbeddedCell => toCellEmbedded(data, 'cell', embedded),
    },
    $tribe: {
        embed: 'tribes',
        transform: (data: Inventory, embedded: Embedded): EmbeddedTribe => toTribeEmbedded(data, 'tribe', embedded),
    },
    $company: {
        embed: 'companies',
        transform: (data: Inventory, embedded: Embedded): EmbeddedCompany => toCompanyEmbedded(data, 'company', embedded),
    },
};

export class Inventory extends Common {
    externalIds?: ExternalId[];
    name: string;
    type: typeEnum;
    cell?: string;
    environment?: string;
    status: statusEnum;
    projects?: string[];
    linkedInventories?: string[];
    dependencies?: string[];
    applications?: string[];
    links?: Link[];
    patchManagement?: PatchManagement;
    fqdn: string;
    subtype: string;
    attributes?: Server;
    mainAttributes?: Record<string, string|number>;
    inventoryUpdatedSource: string;
    inventoryUpdatedAt: string;
    inventoryUpdatedBy: string;
    oneId: string;
    tribe: string;
    company: string;
    serviceLevel: string;

    constructor(data: any) {
        super(data);
        this.id = data.id;
        this.externalIds = data.externalIds;
        this.name = data.name;
        this.type = data.type;
        this.cell = data.cell;
        this.environment = data.environment;
        this.status = data.status;
        this.projects = data.projects;
        this.linkedInventories = data.linkedInventories;
        this.dependencies = data.dependencies;
        this.applications = data.applications;
        this.links = data.links;
        this.patchManagement = data.patchManagement;
        this.fqdn = data.fqdn;
        this.subtype = data.subtype;
        this.attributes = data.attributes;
        this.mainAttributes = data.mainAttributes;
        this.inventoryUpdatedSource = data.inventoryUpdatedSource;
        this.inventoryUpdatedAt = data.inventoryUpdatedAt;
        this.inventoryUpdatedBy = data.inventoryUpdatedBy;
        this.oneId = data.oneId;
        this.tribe = data.tribe;
        this.company = data.company;
        this.serviceLevel = data.serviceLevel;
    }

    public static toInventoryEmbedded<T extends embed>(data: Inventory, embedded: Embedded, customFields: T[]): EmbedInventory<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});

        // TODO
        // hacky
        // metadata has to be convert to map...
        return {...new Inventory(data), ...customData} as EmbedInventory<T>;
    }
}

export interface PatchManagement {
    active?: boolean;
    freeze?: boolean;
    snapshotRequired?: boolean;
    instructions?: string;
    category?: categoryEnum;
    method?: methodEnum;
    frequency?: Frequency;
    portChecks?: number[];
    serviceChecks?: string[];
    softwaresToUpdate?: string[];
    preUpdateScript?: string;
    postUpdateScript?: string;
    maintenanceSchedule?: MaintenanceSchedule;
    patchManagement?: PatchManagement;
}

interface Frequency {
    jan: boolean;
    feb: boolean;
    mar: boolean;
    apr: boolean;
    may: boolean;
    jun: boolean;
    jul: boolean;
    aug: boolean;
    sep: boolean;
    oct: boolean;
    nov: boolean;
    dec: boolean;
}

interface MaintenanceSchedule {
    mon?: string;
    tue?: string;
    wed?: string;
    thu?: string;
    fri?: string;
    sat?: string;
    sun?: string;
}

interface ExternalId {
    source: string;
    id: string;
}

export enum typeEnum {
    SERVER = 'SERVER',
    AZURE = 'AZURE',
    ROUTER = 'ROUTER',
    SWITCH = 'SWITCH',
    STORAGE = 'STORAGE',
    LOAD_BALANCER = 'LOADBALANCER',
}

export enum environmentEnum {
    DEVELOPMENT = 'DEVELOPMENT',
    PRE_PRODUCTION = 'PRE_PRODUCTION',
    PRODUCTION = 'PRODUCTION',
    STAGING = 'STAGING',
    WORK = 'WORK',
}

export enum statusEnum {
    UNKNOWN = 'UNKNOWN',
    BUILDING = 'BUILDING',
    RELEASED = 'RELEASED',
    UNPROVISIONED = 'UNPROVISIONED',
    UNPROVISIONING = 'UNPROVISIONING',
    SUPPLIED = 'SUPPLIED',
    STORED = 'STORED',
    CLOSED = 'CLOSED'
}

export enum categoryEnum {
    PILOT = 'PILOT',
    PRODUCTION_S1 = 'PRODUCTION_S1',
    PRODUCTION_S2 = 'PRODUCTION_S2',
    ENGINEERING = 'ENGINEERING',
    BUILD = 'BUILD',
    INFRA = 'INFRA',
}

export enum methodEnum {
    ANSIBLE = 'ANSIBLE',
    MANUAL = 'MANUAL',
}

export interface Operation {
    path: string;
    op: 'ADD' | 'REMOVE' | 'REPLACE';
    value?: string | string[] | object;
}

export interface BulkEditInventoryResponse {
    updatedInventoryIds: string[];
    errors: { [key: string]: string };
}
